import { useState } from "react";
import NextLink from "next/link";
import { useSession } from "next-auth/client";
import {
  Text,
  Box,
  Link,
  Heading,
  Flex,
  IconButton,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
} from "@chakra-ui/react";
import { formatDistance } from "date-fns";
import { AiOutlineDelete } from "react-icons/ai";
import { FiEdit3 } from "react-icons/fi";
import toast from "react-hot-toast";
import ThreadTweet from "components/tweets/masonry/threadTweet";
import { ConfirmPopup } from "components/popups/confirmPopup";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { getToken } from "firebaseClient";
import { DeleteIcon } from "components/icons/DeleteIcon";
import { PlusIcon } from "components/icons/PlusIcon";

interface Props {
  tweet: any;
  editMode?: boolean;
  onDelete?: (id: string) => void;
}

export function TweetItem({ tweet, editMode = false, onDelete }: Props) {
  const [title, setTitle] = useState<string>("");
  const [isUpdatingTitle, setIsUpdatingTitle] = useState<boolean>(false);
  const [session] = useSession();
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const isAuthenticated = useFirebaseUser();

  const handleDelete = async () => {
    try {
      if (!session && !isAuthenticated) {
        toast.error("User is not authenticated");
        return;
      }

      if (tweet.isFromEz4cast) {
        const dataToSend = {
          tweetId: tweet.id_str
        }
        const token = await getToken(session, "deleteThread");
        let url = "https://us-central1-ez4cast.cloudfunctions.net/tweetThreadsHunter-deleteThread"
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            tokenuserid: session?.user?.uid ?? "",
          },
          body: JSON.stringify(dataToSend),
        });

        const data = await response.json();

        if (!data.success) {
          throw data.error;
        }
      } else {
        const db = firebaseClient.firestore();
        await db
          .collection("users")
          .doc(getAccount(session)?.id)
          .collection("tweets")
          .doc(tweet.id)
          .update({ isGenerateBlog: false });
      }


      onDelete && onDelete(tweet.id);
    } catch (err) {
      console.log("Error in deleting thread: ", err);
      toast.error("Error in deleting thread: " + err.message);
    }
  };

  const handleTitleAdd = async () => {
    try {
      if (!session && !isAuthenticated) {
        toast.error("User is not authenticated");
        return;
      }

      if (title?.length > 90) {
        toast.error("Max 90 characters only");
        return;
      }

      setIsUpdatingTitle(true);

      if (tweet.isFromEz4cast) {
        const dataToSend = {
          tweetId: tweet.id_str,
          blogTitle: title
        }
        const token = await getToken(session, "updateBlogTitle");
        let url = "https://us-central1-ez4cast.cloudfunctions.net/tweetThreadsHunter-updateBlogTitle";
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            tokenuserid: session?.user?.uid ?? "",
          },
          body: JSON.stringify(dataToSend),
        });

        const data = await response.json();

        if (!data.success) {
          throw data.error;
        }

      } else {
        const db = firebaseClient.firestore();
        await db
          .collection("users")
          .doc(getAccount(session)?.id)
          .collection("tweets")
          .doc(tweet.id)
          .update({ blogTitle: title });
      }

      tweet.blogTitle = title;
      onCloseEdit();
      setTitle("");
    } catch (err) {
      console.log("Error in updating title: ", err);
      toast.error("Error in updating title: " + err.message);
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  return (
    <Box>
      <Box mb={(tweet.blogTitle || editMode ? "5" : 0)}>
        {tweet.blogTitle ? (
          <Link
            href={
              "/thread-finder/" +
              tweet.twUserName +
              "/" +
              (tweet.slug ?? tweet.id ?? tweet.id_str)
            }
            isExternal={editMode}
          >
            <Heading fontSize="lg" as="h2" display="inline">
              {tweet.blogTitle}
            </Heading>
          </Link>
        ) : editMode ? (
          <Text color="gray.400" fontSize="lg" display="inline">
            [Add title]
          </Text>
        ) : null}
        {editMode && (
          <>
            <IconButton
              aria-label="edit title"
              icon={<FiEdit3 />}
              variant="tertiary"
              size="sm"
              ml="3"
              onClick={onOpenEdit}
            />
            <IconButton
              aria-label="delete thread"
              icon={DeleteIcon}
              variant="tertiary"
              size="sm"
              onClick={onOpenDelete}
            />
          </>
        )}
      </Box>
      <ThreadTweet
        isDisablePicture={true}
        disableNbChar={true}
        text={tweet.firstTweet ?? tweet.full_text}
        tweet={tweet}
        isLast={true}
        isNotThread={true}
        mode={"blog"}
        disablePreview={false}
      />
      <Flex mt="2" justifyContent="space-between" alignItems="center">
        <Box>
          <Text fontSize="sm" color="gray.400">
            {formatDistance(new Date(tweet.dateSent), new Date(), {
              addSuffix: true,
            })}
          </Text>
        </Box>
        <Box>
          <NextLink
            href={`/thread-finder/${tweet.twUserName}/${
              tweet.slug ?? tweet.id ?? tweet.id_str
            }`}
            passHref
            legacyBehavior>
            <Button
              as={Link}
              bg="gray.800"
              color="white"
              px="4"
              colorScheme="none"
              _hover={{
                textDecor: "none",
              }}
              size="sm"
              borderRadius="xl"
              isExternal={editMode}
            >
              Read it
            </Button>
          </NextLink>
        </Box>
      </Flex>
      <ConfirmPopup
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        cta="Delete"
        title="Delete this thread"
        body="Are you sure you want to delete this thread from the list?"
        allowClickBackground={true}
        ctaProps={{
          colorScheme: "red",
          bg: "#BC1313",
          mx: "auto",
        }}
        callback={handleDelete}
      />
      <Modal isOpen={isOpenEdit} onClose={onCloseEdit}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb="0">Edit thread title</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="8">
            <Text>
              Replace the current title with what you want (90 characters max.).
            </Text>
            <Flex mt="6">
              <Input
                placeholder={tweet.blogTitle}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Button
                variant={"primary"}
                ml="4"
                onClick={handleTitleAdd}
                isLoading={isUpdatingTitle}
                leftIcon={PlusIcon} >
                Add
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
